import Delete from "@mui/icons-material/Delete";
import { type GridRenderCellParams, type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import pages from "../../../../constants/pages";
import {
	emptyGridRowSelectionModel, gridRowSelectionIdsArray,
	gridRowSelectionSize, resolveGridSelectedRowIds
} from "../../../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../../hooks/useDatagridSorting";
import useToastAlert from "../../../../hooks/useToastAlert";
import { selectIsCoAppSuperAdmin, selectOrganizationSsoAndScim, selectUserId, setPermissions } from "../../../../redux/userSlice";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import useCoAppDataGridFilters from "../../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppStandardDataGrid from "../../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppAddItemsToListModal from "../../../global/components/modals/CoAppAddItemsToListModal";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../global/components/modals/CoAppEditNameModal";
import SettingsDataGridSelectionRow, { destructiveIconSx } from "../../SettingsDataGridSelectionRow";
import { getRoleUsersColumns, type RoleUserRow } from "./columns/role-users-columns";

/** Passed from `RoleWithErrorBoundary` when the route was opened without `navigate(..., { state })` (e.g. sidebar). */
interface RoleNavState {
	name: string;
	isSuperAdmin?: boolean;
}

interface RoleUsersProps {
	state?: RoleNavState | null;
}

interface UserToAdd {
	id: string | number;
	name: string;
	email: string;
	firstname?: string;
	lastname?: string;
}

export default function RoleUsers({ state: stateFromBoundary }: RoleUsersProps = {}) {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("roleUsers");
	const { viewUserPage, updateCoAppNavigation } = useCoAppNavigation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { roleId: roleIdParam } = useParams<{ roleId: string }>();
	const roleId = roleIdParam ?? "";
	const { pathname, state: stateFromLocation } = useLocation() as {
		pathname: string;
		state: RoleNavState | undefined;
	};
	/* Router `location.state` only exists when navigation passes `{ state }`. Otherwise the parent boundary injects `state` after fetching the role. */
	const state: RoleNavState = stateFromBoundary ?? stateFromLocation ?? { name: "" };
	const organizationSsoStatus = useSelector(selectOrganizationSsoAndScim);
	const organizationSsoEnabled = organizationSsoStatus ?? false;
	const currentUserId = useSelector(selectUserId);
	const currentUserIsCoAppSuperAdmin = useSelector(selectIsCoAppSuperAdmin);

	const [availableUsers, setAvailableUsers] = useState<UserToAdd[]>([]);
	const [selectedUsers, setSelectedUsers] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [roleUsers, setRoleUsers] = useState<RoleUserRow[]>([]);
	const [userSeed, setUserSeed] = useState(1);
	const [usersToAdd, setUsersToAdd] = useState<UserToAdd[]>([]);
	const [addUsersToggled, setAddUsersToggled] = useState(false);
	const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
	const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
	const [removeUsersIsToggled, setRemoveUsersIsToggled] = useState(false);
	const [roleNameInput, setRoleNameInput] = useState("");
	const [roleNameError, setRoleNameError] = useState("");
	const [loading, setLoading] = useState(true);
	const [optionsLoading, setOptionsLoading] = useState(true);

	const handleCurrentUserPermissionUpdate = () => {
		if (currentUserId == null) return;
		axios.get(apiRoutes.getUser(1, currentUserId, "", ""), { headers: authHeader })
			.then(res => {
				dispatch(setPermissions(res.data.permissions));
			})
			.catch(err => console.log(err));
	};
	
	const init = () => {
		if (!roleId) return;
		const userQueryObject = { roleId };
		axios.get(apiRoutes.getUsers, {
			headers: authHeader,
			params: userQueryObject,
		})
			.then((res) => {
				const rows = (res.data as RoleUserRow[]).map((user) => {
					const nested = user.user;
					const fn = (user.firstname as string | undefined) ?? nested?.firstname;
					const ln = (user.lastname as string | undefined) ?? nested?.lastname;
					const em = (user.email as string | undefined) ?? nested?.email ?? "";
					return {
						...user,
						firstname: fn,
						lastname: ln,
						email: em,
						user: {
							...nested,
							firstname: fn ?? "",
							lastname: ln ?? "",
							email: em,
						},
					};
				});
				setLoading(false);
				setRoleUsers(rows);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const handleUserStatusToggle = (id: RoleUserRow["id"], active: "false" | "true") => {
		const userJSON = {
			active
		};
		axios.put(apiRoutes.setUserActive(id), userJSON, { headers: authHeader })
			.then(() => {
				setUserSeed(Math.random());
			})
			.catch((error) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.USER_TOGGLE_ERROR_MSG);
			});
	};

	const removeUserFromRole = (userId: RoleUserRow["id"]) => {
		if (!roleId) return;
		axios.delete(apiRoutes.removeUserFromRole(roleId, userId), { headers: authHeader })
			.then(() => {
				setUserSeed(Math.random());
				const currentUserIsInCurrentRolesUsers = roleUsers.filter(user => user.id === currentUserId).length > 0;
				if (currentUserIsInCurrentRolesUsers) {
					handleCurrentUserPermissionUpdate();
				}
			})
			.catch((error) => {
				console.log(error);
				handleToastAlert(messageLevels.ERROR, messages.USER_REMOVED_ERROR_MSG);
			});
	};
	
	const viewUserPageFromRoleGrid = (
		params: GridRenderCellParams<RoleUserRow>,
		navIndex: number,
		rid: string,
		stateName: string
	) => viewUserPage(params as unknown as Parameters<typeof viewUserPage>[0], navIndex, rid, stateName);

	const columns = useMemo(() => (
		getRoleUsersColumns(
			organizationSsoEnabled,
			navigationItemReferences,
			viewUserPageFromRoleGrid,
			roleId,
			state.name,
			removeUserFromRole,
			handleUserStatusToggle,
			currentUserIsCoAppSuperAdmin
		)
	), [organizationSsoEnabled, navigationItemReferences, roleId, state.name, currentUserIsCoAppSuperAdmin]);

	const addUsersToRole = () => {
		if (!roleId) return;
		const userIds = usersToAdd.map(u => u.id);
		axios.post(apiRoutes.addUsersToRole(roleId), { userIds: userIds }, {
			headers: authHeader
		}).then(() => {
			setUserSeed(userSeed + 1);
			toggleAddNewDialog();
			setUsersToAdd([]);
			const currentUserIsInCurrentRolesUsers = roleUsers.filter(user => user.id === currentUserId).length > 0;
			if (currentUserIsInCurrentRolesUsers) {
				handleCurrentUserPermissionUpdate();
			}
		}).catch(err => {
			console.error(err);
			handleToastAlert(messageLevels.ERROR, messages.USERS_ADDED_ERROR_MSG);
		});
	};

	const addUserToUsersToAdd = (value: string[]) => {
		/* Options = `availableUsers` only — never match `roleUsers` first (name collisions, wrong id) */
		const next = value
			.map((displayName): UserToAdd | null => {
				const found = availableUsers.find((row) => row.name === displayName);
				if (!found) return null;
				const display =
					found.name === found.email
						? found.email
						: `${found.firstname ?? ""} ${found.lastname ?? ""}`.trim() || (found.email ?? "");
				return { ...found, name: display, email: found.email ?? "" };
			})
			.filter((u): u is UserToAdd => u != null);
		setUsersToAdd(next);
	};

	const initUsersForAdd = () => {
		axios
			.get(apiRoutes.getUsers, {
				headers: authHeader,
			})
			.then((res) => {
				let users = res.data.filter((user) => {
					return !roleUsers.some((roleUser) => roleUser.id === user.id);
				});
				users = users.map((user) => {
					return {
						id: user.id,
						name: user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : user.email || "",
						email: user.email || "",
						firstname: user.firstname,
						lastname: user.lastname
					};
				});
				setAvailableUsers(users);
				setOptionsLoading(false);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const bulkRemoveUsersFromRole = () => {
		if (!roleId) return;
		const userIds = gridRowSelectionIdsArray(selectedUsers, roleUsers);
		const deleteData = { userIds };
		const plural = userIds.length > 1;
		axios.delete(apiRoutes.bulkRemoveUsersFromRole(roleId), { headers: authHeader, data: deleteData })
			.then(() => {
				setUserSeed(Math.random());
				setRemoveUsersIsToggled(false);
				setSelectedUsers(emptyGridRowSelectionModel());
			})
			.catch((error) => {
				console.log(error);
				handleToastAlert(messageLevels.ERROR, messages.USER_REMOVED_ERROR_MSG);
			});
	};

	const toggleDeleteRoleDialog = () => {
		setDeleteRoleDialogOpen(!deleteRoleDialogOpen);
	};

	const deleteRole = () => {
		if (!roleId) return;
		axios.delete(apiRoutes.deleteRole(roleId), {
			headers: authHeader
		}).then(() => {
			navigate(pages.roleManagement, { replace: true });
			updateCoAppNavigation(true, navigationItemReferences.roleNavItemIndex);
			const currentUserIsInCurrentRolesUsers = roleUsers.filter(user => user.id === currentUserId).length > 0;
			if (currentUserIsInCurrentRolesUsers) {
				handleCurrentUserPermissionUpdate();
			}
			toggleDeleteRoleDialog();
		}).catch((err) => {
			console.log(err);
			handleToastAlert(messageLevels.ERROR, messages.ROLE_DELETION_ERROR_MSG);
			toggleDeleteRoleDialog();
		});
	};

	const toggleEditRoleDialog = () => {
		setEditRoleDialogOpen(!editRoleDialogOpen);
		setRoleNameInput("");
		setRoleNameError("");
	};

	const handleRoleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setRoleNameInput(e.target.value);
		setRoleNameError("");
	};

	const handleRoleNameChangeSubmit = (e) => {
		if (!roleNameInput) return;

		if (roleNameInput === state.name) {
			toggleEditRoleDialog();
			return;
		}

		if (!roleId) return;
		axios.put(apiRoutes.updateRole(roleId), { name: roleNameInput }, { headers: authHeader })
			.then(() => {
				toggleEditRoleDialog();
				updateCoAppNavigation(false, navigationItemReferences.roleNavItemIndex, roleNameInput, pathname);
			})
			.catch((e: unknown) => {
				console.log(e);
				const msg = axios.isAxiosError(e) ? String(e.response?.data?.message ?? "") : "";
				if (msg.includes("already exists")) {
					setRoleNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Role"));
					return;
				}
				toggleEditRoleDialog();
				handleToastAlert(messageLevels.ERROR, messages.ROLE_NAME_UPDATE_ERROR_MSG);
			});
	};

	const toggleAddNewDialog = () => {
		if (!addUsersToggled) {
			initUsersForAdd();
			setAddUsersToggled(true);
		} else {
			setAddUsersToggled(false);
			setAvailableUsers([]);
			setUsersToAdd([]);
			setOptionsLoading(true);
		}
	};

	useEffect(() => {
		init();
		handleInitializeSortModel();
	}, [userSeed, state.name]);

	const {
		apiRef,
		gridFilterModel,
		onFilterModelChange,
		quickFilterSearchProps,
		visibleRowCount,
		filterControl,
		filterButtonEl,
	} = useCoAppDataGridFilters(roleUsers, loading);
	const selectedUserCount = resolveGridSelectedRowIds(selectedUsers, roleUsers).length;

	return (
		<>
			<CoAppDatagridHeader
				title={`${state.name} User Management`}
				resourceType="Role"
				subResourceType="User"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				addIsPresent={organizationSsoStatus || state.isSuperAdmin ? false : true}
				addOnClickHandler={toggleAddNewDialog}
				editIsPresent={state.isSuperAdmin ? false : true}
				editOnClickHandler={toggleEditRoleDialog}
				deleteIsPresent={state.isSuperAdmin ? false : true}
				deleteOnClickHandler={toggleDeleteRoleDialog}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				columns={columns}
				rows={roleUsers}
				pinnedColumns={["actions"]}
				allowSelection={organizationSsoStatus ? false : true}
				targetResource="users"
				parentResource="role"
				parentResourceName={state.name}
				selectionModel={selectedUsers}
				setSelectionModel={setSelectedUsers}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun="user"
						visibleCount={visibleRowCount}
						selectedCount={selectedUserCount}
						loading={loading}
						onClearSelection={() => setSelectedUsers(emptyGridRowSelectionModel())}
						actions={organizationSsoStatus ? undefined : [{
							label: "Remove",
							icon: <Delete sx={destructiveIconSx} />,
							onClick: () => setRemoveUsersIsToggled(true),
							variant: "destructive",
						}]}
					/>
				}
			/>
			<CoAppAddItemsToListModal
				addButtonTitle="Save"
				inputLabel="User(s)"
				addItemsIsToggled={addUsersToggled}
				addItemsToCollection={addUsersToRole}
				addItemsToNewArray={addUserToUsersToAdd}
				dialogTitle={`Add user(s) to ${state.name} role`}
				items={availableUsers}
				itemsToAdd={usersToAdd}
				toggleDialog={toggleAddNewDialog}
				loading={optionsLoading}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteRoleDialogOpen}
				dialogTitle={`Delete ${state.name} role?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(state.name, "role")}
				confirmClickHandler={deleteRole}
				cancelClickHandler={toggleDeleteRoleDialog}
				actionText="Delete"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={removeUsersIsToggled}
				dialogTitle={`Remove ${gridRowSelectionSize(selectedUsers, roleUsers) > 1 ? "users" : "user"} from ${state.name} role?`}
				dialogMessage={`Are you sure that you want to remove the selected ${gridRowSelectionSize(selectedUsers, roleUsers) > 1 ? "users" : "user"} from the ${state.name} role?`}
				confirmClickHandler={bulkRemoveUsersFromRole}
				cancelClickHandler={() => setRemoveUsersIsToggled(false)}
				actionText="Remove"
			/>
			<CoAppEditNameModal
				dialogOpen={editRoleDialogOpen}
				dialogTitle="Edit role name"
				changeHandler={handleRoleNameChange}
				placeholderText={state.name}
				confirmClickHandler={handleRoleNameChangeSubmit}
				cancelClickHandler={toggleEditRoleDialog}
				actionText="Save"
				editNameError={roleNameError}
				setEditNameError={setRoleNameError}
			/>
		</>
	);
}